import { type ButtonHTMLAttributes, type MouseEventHandler, type ReactNode } from 'react';
import { Link as RouterLink, type LinkProps as RouterLinkProps } from 'react-router-dom';
import { type VariantProps } from 'class-variance-authority';
import { ChevronRightIcon } from '@/components/icons';
import { cn } from '@/utils/cn';
import { buttonVariants, TRANSPARENT_UNDERLINE_CLASSES } from './buttonVariants';

type Variants = VariantProps<typeof buttonVariants>;

export interface ButtonProps extends Variants {
  iconRight?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;

  /** Internal route — renders <Link to>. */
  to?: RouterLinkProps['to'];
  /** External URL — renders <a href>. */
  href?: string;
  /** When href is set, open in a new tab with rel="noopener noreferrer". */
  external?: boolean;

  /** Native <button> props (only applied when not rendered as a link). */
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  name?: string;
  form?: string;

  onClick?: MouseEventHandler<HTMLElement>;
  className?: string;
  children?: ReactNode;

  id?: string;
  'aria-label'?: string;
  'aria-haspopup'?: ButtonHTMLAttributes<HTMLButtonElement>['aria-haspopup'];
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-current'?: ButtonHTMLAttributes<HTMLButtonElement>['aria-current'];
}

function Spinner() {
  return (
    <svg
      className="size-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

/**
 * Design-system Button. Mirrors the figma component path:
 *
 *   Button / Transparent / Large / Black            — nav-pill page links
 *   Button / Transparent / Small / Blue + iconRight — inline blue chevron CTAs
 *   Button / Transparent / Small / Black            — footer page links
 *   Button / Outlined    / Large / Blue             — pill outlined CTAs
 *
 * Dispatches to <button>, <Link to>, or <a href> based on which prop is supplied.
 */
export function Button({
  variant = 'transparent',
  size = 'large',
  tone = 'black',
  iconRight = false,
  isLoading = false,
  fullWidth = false,
  to,
  href,
  external = false,
  type,
  disabled,
  name,
  form,
  onClick,
  className,
  children,
  ...aria
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, tone }), fullWidth && 'w-full', className);

  const labelClasses = variant === 'transparent' ? TRANSPARENT_UNDERLINE_CLASSES : undefined;

  const inner = (
    <>
      {isLoading ? <Spinner /> : null}
      {children != null ? <span className={labelClasses}>{children}</span> : null}
      {iconRight && !isLoading ? <ChevronRightIcon className="size-4" /> : null}
    </>
  );

  if (to !== undefined) {
    return (
      <RouterLink to={to} onClick={onClick} className={classes} {...aria}>
        {inner}
      </RouterLink>
    );
  }

  if (href !== undefined) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={classes}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...aria}
      >
        {inner}
      </a>
    );
  }

  const isDisabled = disabled ?? isLoading;
  return (
    <button
      type={type ?? 'button'}
      disabled={isDisabled}
      name={name}
      form={form}
      aria-busy={isLoading || undefined}
      aria-disabled={isDisabled || undefined}
      onClick={onClick}
      className={classes}
      {...aria}
    >
      {inner}
    </button>
  );
}
